import { getFilterData, getValidEntities, type ServiceOption } from '@/lib/nomenclature/appointmentRepo';
import { getTimetable } from '../api/booking.api';
import type { BookingSelection, ResolvedSlot } from './types';

/**
 * "Primul loc disponibil" search — faithful port of the mobile
 * `FirstAvailableManager`. Searches up to 12 months of timetable data for the
 * earliest slot that matches the current filters, then resolves which service
 * the returned doctor actually offers.
 */

/** Priority consult types, in the exact order the app walks them. */
export const PRIORITY_INVESTIGATION_TYPES = [
  'Consult - Medic primar',
  'Consult - Medic specialist',
  'Consult - Profesor',
  'Consult - radioterapie',
  'Consult - oncologie',
  'Consult - oncologie / radioterapie',
  'Consult - Medic primar alergologie',
  'Consult - Medic primar - chirurgie',
  'Consult - Medic primar chirurgie generala',
];

const MONTHS_TO_SEARCH = 12;
/** A slot must be at least this far in the future to be bookable. */
const MIN_LEAD_MS = 2 * 60 * 60 * 1000;

export class NoSlotError extends Error {}

/** Is the first-available button enabled for the current selection? */
export function isFirstAvailableActive(
  selection: BookingSelection,
  availableServices: ServiceOption[],
): boolean {
  const required = !!selection.city && !!selection.location && !!selection.specialty;
  const hasPriority = availableServices.some((s) =>
    PRIORITY_INVESTIGATION_TYPES.some((p) => s.name.toLowerCase().includes(p.toLowerCase())),
  );
  return required && (hasPriority || !!selection.service);
}

function refMonth(base: Date, offset: number): string {
  const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * After a slot's doctor is known, choose the service that doctor actually
 * offers — keep the selected one if valid, else walk the priority list in
 * order, else any service the doctor offers.
 */
async function resolveServiceForDoctor(
  selection: BookingSelection,
  physicianDbId: number | null,
): Promise<{ name: string; service: ServiceOption | null }> {
  let doctorServices: ServiceOption[] = [];
  if (physicianDbId != null && physicianDbId !== 0) {
    try {
      const data = await getFilterData({
        cityId: selection.city?.id,
        locationId: selection.location?.id,
        specialtyId: selection.specialty?.id,
        physicianId: physicianDbId,
      });
      doctorServices = data.services;
    } catch {
      // fall through with empty list
    }
  }

  const selected = selection.service;
  if (selected) {
    const offersSelected = doctorServices.some((s) => s.id === selected.id);
    if (offersSelected || doctorServices.length === 0) return { name: selected.name, service: selected };
  }

  for (const priority of PRIORITY_INVESTIGATION_TYPES) {
    for (const service of doctorServices) {
      if (service.name.toLowerCase().includes(priority.toLowerCase())) {
        return { name: service.name, service };
      }
    }
  }

  if (doctorServices.length > 0) {
    const fallback = doctorServices[0]!;
    return { name: fallback.name, service: fallback };
  }
  return { name: '-', service: selected };
}

export async function searchFirstAvailable(selection: BookingSelection): Promise<ResolvedSlot> {
  const entities = await getValidEntities({
    locationId: selection.location?.id,
    specialtyId: selection.specialty?.id,
    physicianId: selection.doctor?.id,
    investigationId: selection.service?.id,
  });

  if (entities.length === 0) throw new NoSlotError('Căutarea nu a dat niciun rezultat.');

  const validEntityIds = new Set(entities.map((e) => e.entityId));
  const entityByEntityId = new Map(entities.map((e) => [e.entityId, e] as const));

  // When a service is selected, restrict candidate physicians to those that
  // actually offer it (authoritative azure_filter_combinations source).
  let validPhysicianIds = new Set<number>();
  if (selection.service?.id != null) {
    try {
      const filterData = await getFilterData({
        cityId: selection.city?.id,
        locationId: selection.location?.id,
        specialtyId: selection.specialty?.id,
        investigationId: selection.service.id,
      });
      validPhysicianIds = new Set(filterData.doctors.map((d) => d.id));
    } catch {
      // leave empty → no extra constraint
    }
  }

  const base = new Date();

  for (let i = 0; i < MONTHS_TO_SEARCH; i++) {
    let days;
    try {
      days = await getTimetable({
        physicianId: selection.doctor?.physicianId,
        specialityId: selection.specialty?.specialityId,
        locationId: selection.location?.locationId,
        refMonth: refMonth(base, i),
      });
    } catch {
      continue; // try next month
    }

    const slot = await findEarliestSlot(days, selection, entityByEntityId, validEntityIds, validPhysicianIds);
    if (slot) return slot;
  }

  throw new NoSlotError(`Nu s-a găsit niciun loc disponibil în următoarele ${MONTHS_TO_SEARCH} luni.`);
}

async function findEarliestSlot(
  days: Awaited<ReturnType<typeof getTimetable>>,
  selection: BookingSelection,
  entityByEntityId: Map<string, { physicianId: number; physicianName: string }>,
  validEntityIds: Set<string>,
  validPhysicianIds: Set<number>,
): Promise<ResolvedSlot | null> {
  const now = Date.now();
  const minBooking = now + MIN_LEAD_MS;

  const sorted = [...days].sort((a, b) => a.day.getTime() - b.day.getTime());

  for (const day of sorted) {
    if (!day.entityId || !validEntityIds.has(day.entityId)) continue;

    // Resolve the day's physician (selected doctor wins, else from the entity).
    let physicianDbId = selection.doctor?.id ?? null;
    if (physicianDbId == null) {
      physicianDbId = entityByEntityId.get(day.entityId)?.physicianId ?? null;
    }

    if (validPhysicianIds.size > 0 && (physicianDbId == null || !validPhysicianIds.has(physicianDbId))) {
      continue; // physician doesn't offer the selected service
    }

    const intervals = [...day.intervals].sort((a, b) => a.start.getTime() - b.start.getTime());
    for (const interval of intervals) {
      if (interval.available === false) continue;
      if (interval.start.getTime() < minBooking) continue;

      const doctorName = selection.doctor?.name ?? entityByEntityId.get(day.entityId)?.physicianName ?? '-';
      const { name: serviceName, service } = await resolveServiceForDoctor(selection, physicianDbId);

      return {
        doctorName,
        physicianId: selection.doctor?.physicianId ?? day.physicianId ?? '',
        physicianDbId,
        serviceName,
        service,
        entityId: day.entityId,
        day: day.day,
        start: interval.start,
        end: interval.end,
        locationName: selection.location?.name ?? 'Locația medicală',
        cityName: selection.city?.name ?? '',
      };
    }
  }

  return null;
}
